import { useState } from 'react';
import { Alert } from '@pryme/ui';
import { CodeBlock } from '../components/CodeBlock';
import { useServiceOutlet } from '../components/ServiceLayout';
import { agentSnippet, browserSnippet, envExample, expressSnippet, honoSnippet, signatureBaseExample, snippetValues } from '../lib/snippets';

const TABS = [
  { id: 'express', label: 'Express' },
  { id: 'hono', label: 'fetch / Hono / Workers' },
  { id: 'browser', label: 'Browser button' },
  { id: 'agent', label: 'Signed agent request' },
  { id: 'raw', label: 'Without the SDK' },
] as const;

export function SdkIntegration() {
  const { service: s } = useServiceOutlet();
  const v = snippetValues(s);
  const [tab, setTab] = useState<(typeof TABS)[number]['id']>('express');
  return (
    <>
      <Alert tone="warn">
        <strong>Server credentials stay on your backend.</strong> Never ship <code>PRYME_SECRET_KEY</code> in browser or mobile code. The browser only navigates to your <code>/login</code> route.
      </Alert>
      <section className="p-card" aria-labelledby="env-h">
        <h2 id="env-h">Configuration</h2>
        <p className="p-small p-muted">Your service’s real public identifiers. The secret key comes from the one-time credentials panel or your downloaded <code>.env</code>.</p>
        <CodeBlock label=".env" code={envExample(v)} />
        <CodeBlock label="Install" code="npm install @pryme/sdk" />
      </section>

      <section className="p-card" aria-labelledby="int-h">
        <h2 id="int-h">Integrate</h2>
        <div className="dv-tabs" role="tablist" aria-label="Integration variant">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} aria-controls={`panel-${t.id}`} id={`tab-${t.id}`} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === 'express' && (
            <>
              <p className="p-small">Sign-in (Authorization Code + PKCE), callback with signed code exchange, <code>verify</code> for sessions and agent requests, and <code>authorize</code> for transaction-specific mandate checks.</p>
              <CodeBlock label="server.ts" code={expressSnippet(v)} />
            </>
          )}
          {tab === 'hono' && (
            <>
              <p className="p-small">The SDK is fetch-native (WebCrypto + audited @noble Ed25519) and runs on Cloudflare Workers, Deno, Bun and Node 20+.</p>
              <CodeBlock label="worker.ts" code={honoSnippet(v)} />
            </>
          )}
          {tab === 'browser' && <CodeBlock label="index.html" code={browserSnippet(v)} />}
          {tab === 'agent' && (
            <>
              <p className="p-small">
                Agents are separate principals with their own keys, registered by a person in the Pryme portal. They sign their <em>actual</em> request to your API and carry the mandate the person signed. Your backend verifies it with <code>pryme.verify(req)</code> (see the Express tab).
              </p>
              <CodeBlock label="agent.ts" code={agentSnippet(v)} />
            </>
          )}
          {tab === 'raw' && (
            <>
              <p className="p-small">Any language can integrate: sign this exact UTF-8 string with your signing key (Ed25519) and send it base64url-encoded in <code>pryme-signature: ed25519=…</code>.</p>
              <CodeBlock label="Signature base (newline-separated)" code={signatureBaseExample(v)} />
              <CodeBlock
                label="Headers"
                code={`pryme-service: ${v.serviceId}\npryme-key-id: ${v.keyId}\npryme-timestamp: <same as above>\npryme-nonce: <same as above>\npryme-content-digest: sha-256=<…>\npryme-signature: ed25519=<base64url signature>\ncontent-type: application/json`}
              />
              <p className="p-small">Then verify the <code>pryme-response-signature</code> JWS (typ <code>pryme-response-v1</code>) with Pryme's verification key (inside <code>PRYME_SECRET_KEY</code>; see the credentials panel), and check <code>service_id</code>, <code>request_nonce</code>, <code>status</code> and <code>body_digest</code>. Test vectors: <code>packages/protocol/fixtures/signature-vectors.json</code>.</p>
            </>
          )}
        </div>
      </section>

      <section className="p-card" aria-labelledby="claims-h">
        <h2 id="claims-h">Use claims</h2>
        <CodeBlock
          label="Predicates"
          code={`const id = await pryme.verify(req, { session });
id.isGenuine();            // full trust chain: Pryme attestation (binding) → pairwise key → state, audience, expiry
id.isHumanVerified();      // issuer-backed assurance (sandbox evidence only in sandbox mode)
id.isOver(18);             // false unless disclosed AND true — fails closed
id.isResidentOf('DK');
id.claimStatus('age.over18'); // 'true' | 'false' | 'not_disclosed' | 'unavailable' | 'stale' | 'rejected'
id.assurance();            // { environment, category, level, provider, scheme, verified_at }`}
        />
      </section>
    </>
  );
}
